import React, { useEffect, useRef, useState } from 'react';
import './SearchBar.css';
import IconButton from '../button/IconButton';
import { useAtomValue, useSetAtom } from 'jotai';
import { mainMapAtom, mapClickEventAtom, menuItemsAtom, searchResultLngLatAtom, selectedLayersAtom } from '../../atoms';
import { extractCoordinates } from '../../lib/search/extractCoordinates';
import classNames from 'classnames';
import { CircularProgress } from '@mui/material';
import { clearResult, highlightFeature } from '../../lib/objectHighlight';
import { searchFeatures } from '../../lib/search/searchFeatures';
import { MapGeoJSONFeature } from 'maplibre-gl';
import { filterdFeatures } from '../../lib/filtering/filterdFeatures';
import { searchAddress } from '../../lib/search/searchAddress';
import * as Sentry from "@sentry/react";

type resType = 'error' | 'success' | 'warning';
type searchResultsType = {
    text: string;
    type: resType;
}


const SearchBar: React.FC = () => {
    const menuItems = useAtomValue(menuItemsAtom);
    const mainMap = useAtomValue(mainMapAtom);
    const setSearchResultLngLat = useSetAtom(searchResultLngLatAtom);

    const lookforResFeatures = useRef<MapGeoJSONFeature[] | undefined>(undefined);

    const [searchResults, setSearchResults] = useState<searchResultsType | undefined>(undefined); // 検索結果を保持する状態
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [searchEnd, setSearchEnd] = useState<boolean>(false);
    
    const selectedLayers = useAtomValue(selectedLayersAtom);
    const setClickEvent = useSetAtom(mapClickEventAtom);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        setSearchTerm(event.target.value);
        setSearchEnd(false);
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
    };

    /* *********************
     * クリアボタンクリック時処理
     * *********************/
    const onClickClearBtn = () => {
        setSearchTerm('');
        setSearchEnd(false);
        clearResult(mainMap, lookforResFeatures.current);
        lookforResFeatures.current = undefined;
        setSearchResults(undefined);
    }

    /* *********************
     * 住所検索引っかからなかった場合、フォームへ移動
     * *********************/ 
    // const transitionToForm = () => {
    //     window.open('https://forms.gle/kzyWbDqLJ3XvqomY9', '_blank');
    // };

    /* *********************
     * 検索ボタンクリック時の処理
     * *********************/ 
    const onClickSearchBtn = async () => {
        if(!mainMap) { return; }
        
        clearResult(mainMap, lookforResFeatures.current);
        lookforResFeatures.current = undefined;

        if(searchTerm === '') { return; }

        setIsLoading(true);

        const coordinates = extractCoordinates(searchTerm);

        // ① 緯度経度を抽出できた場合、移動する
        if (coordinates) {
            mainMap.flyTo({ center: coordinates as [number, number], zoom: 15 });
            setSearchResultLngLat(coordinates as [number, number]);
            setSearchResults({
                text: '指定の座標に移動します',
                type: 'success'
            });
            setIsLoading(false);
            setSearchEnd(true);
            return;
        }


        // ② キーワード検索（レイヤー選択時のみ）
        const features = filterdFeatures(mainMap.queryRenderedFeatures(), menuItems);
        const result = searchFeatures(features, searchTerm);

        if(result) {
            lookforResFeatures.current = result;

            // 結果のfeatureを新規レイヤーとして追加する
            const uniqueFeatureCount = highlightFeature(mainMap, result, true);

            setSearchResults({
                text: `${uniqueFeatureCount}件 ヒットしました`,
                type: 'success'
            });
            setIsLoading(false);
            setSearchEnd(true);
            setClickEvent(undefined);
            return;
        }

        
        // ③ 住所検索
        let res: Awaited<ReturnType<typeof searchAddress>>;
        try {
            res = await searchAddress(searchTerm);
        } catch (error) {
            Sentry.captureException(error);
            setSearchResults({
                text: '住所検索でエラーが発生しました',
                type: 'warning'
            });
            setIsLoading(false);
            setSearchEnd(true);
            return;
        }

        if(res) {
            setSearchResults({
                text: res.message,
                type: res.type
            });

            mainMap.flyTo({ center: [res.point.lng, res.point.lat], zoom: 15 });
            setSearchResultLngLat([res.point.lng, res.point.lat]);
            setIsLoading(false);
            setSearchEnd(true);
            return;
        }

        // ④ 検索結果がない場合
        setSearchResults({
            text: '検索結果がありません',
            type: 'error'
        });

        setIsLoading(false);
        setSearchEnd(true);
    };


    // 選択中のレイヤーが変わったら、検索結果をクリア
    useEffect(() => {
        if(selectedLayers) {
            clearResult(mainMap, lookforResFeatures.current);
            lookforResFeatures.current = undefined;
        }
    }, [selectedLayers]);



    return (
        <div className='searchbar-wrapper'>
            <form onSubmit={handleSubmit} className='searchbar'>
                <input
                    type="text"
                    placeholder="例: 焼津市本町2丁目 / 34.867,138.322"
                    value={searchTerm}
                    onChange={handleInputChange}
                    title="住所または座標で検索できます。左のメニューにチェックを入れて、データを地図上に表示し、キーワードで検索することができます。" // ツールチップ
                    style={{ fontSize: '14px' }}
                />
                <IconButton onClick={onClickSearchBtn} icon='search' />
                { isLoading ? <CircularProgress size="20px" /> : searchEnd && <IconButton onClick={onClickClearBtn} icon='close' /> }
            </form>
            {searchResults && (
                <div className={classNames(
                        'search-results-tooltip', 
                        {
                            'success': searchResults.type === 'success',
                            'error': searchResults.type === 'error',
                            'warning': searchResults.type === 'warning'
                        }
                    )}
                >
                    <p>{ searchResults.text }</p>
                    { searchResults.type === 'error' && 
                        <>
                            <hr />
                            <div className='error-msg-item'>
                                <p className='error-msg-title'>入力した緯度経度が引っかからなかった場合</p> 
                                <p className='error-msg-description'>「緯度、経度」または「経度、緯度」の形式で再度お試しください。（例：34.876、138.322）</p>
                            </div>
                            <hr />
                            <div className='error-msg-item'>
                                <p className='error-msg-title'>入力した属性情報が引っかからなかった場合</p>
                                <p className='error-msg-description'>左のメニューから、データを選択し直して再度お試しください。</p>
                            </div>
                        </>
                    }
                    {/* { (searchResults.type === 'warning' || searchResults.type === 'error') &&
                        <>
                            <hr />
                            <div className='error-msg-item'>
                                <p className='error-msg-title'>入力した住所が引っかからなかった場合</p> 
                                <p className='error-msg-description'>検索時に使用する住所は整理中です。</p>
                                { <p className='error-msg-description'>お手数ですが入力した住所を以下フォームに入力し、お知らせください。</p>}
                            </div>
                            { <SquareButton label='住所を入力' onClick={transitionToForm} fullWidth color='secondary' bold /> }
                        </>
                    } */}
                </div>
            )}
        </div>
    );
};

export default SearchBar;
